import type { FertilityTestingState } from "./FertilityTestingContext";
import { paths } from "@src/packages/openapi/schema";
import { RequestBody } from "@src/packages/openapi/helpers";
import type { TestResultCreate } from "@louise/medical-history/basic-info/types";
import { isFertilityTestingResult } from "./isFertilityTestingResult";

type UpdatePatientRequest = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;
type TestResultOperation = NonNullable<
  UpdatePatientRequest["testResults"]
>[number];
type ApiTestResultType = Extract<
  Exclude<TestResultOperation, { op: "delete" }>["type"],
  string
>;

function toApiTestResultType(
  type: TestResultCreate["type"],
): ApiTestResultType | undefined {
  if (!type) return undefined;
  if (type === "semen_fluid") return "semenFluidAnalysis";
  if (type === "other") return undefined;
  return type;
}

export function transformFertilityTestingStateToRequest(
  state: FertilityTestingState,
): RequestBody<paths["/{tenantId}/patients/{patientId}"]["patch"]> {
  const operations: TestResultOperation[] = [];

  // Create set of IDs to delete for quick lookup (prevents double-sending)
  const idsToDeleteSet = new Set(state.testResultIdsToDelete || []);

  // Add deletions from testResultIdsToDelete
  state.testResultIdsToDelete.forEach((id) => {
    if (id) {
      operations.push({ id, op: "delete" });
    }
  });

  // If user answered "No" to having testing done, delete all existing tests
  // (those with IDs that haven't already been marked for deletion)
  if (state.hasTestingDone === false) {
    state.testResults.filter(isFertilityTestingResult).forEach((testResult) => {
      if (testResult.id && !idsToDeleteSet.has(testResult.id)) {
        operations.push({ id: testResult.id, op: "delete" });
      }
    });
    // Return early with delete operations only - no create/update when user said "No"
    return {
      testResults: operations.length > 0 ? operations : [],
    };
  }

  // Add updates/creates (excluding those marked for deletion)
  state.testResults
    .filter(
      (testResult) =>
        isFertilityTestingResult(testResult) &&
        (!testResult.id || !idsToDeleteSet.has(testResult.id)),
    )
    .forEach((testResult) => {
      const data: Exclude<TestResultOperation, { op: "delete" }> = {};
      const apiType = toApiTestResultType(testResult.type);
      if (apiType) {
        data.type = apiType;
      }

      if (apiType === "urine" && testResult.urineTestCategory) {
        data.urineTestCategory = testResult.urineTestCategory;
      } else if (apiType === "blood" && testResult.bloodTestCategory) {
        data.bloodTestCategory = testResult.bloodTestCategory;
      } else if (apiType === "imaging" && testResult.imagingTestCategory) {
        data.imagingTestCategory = testResult.imagingTestCategory;
      } else if (apiType === "sampling" && testResult.samplingTestCategory) {
        data.samplingTestCategory = testResult.samplingTestCategory;
      } else if (apiType === "genetics" && testResult.geneticsTestCategory) {
        data.geneticsTestCategory = testResult.geneticsTestCategory;
      } else if (
        apiType === "semenFluidAnalysis" &&
        testResult.semenFluidTestCategory
      ) {
        data.semenFluidTestCategory = testResult.semenFluidTestCategory as
          | "basic"
          | "extended";
      } else if (apiType === "clinical" && testResult.clinicalTestCategory) {
        data.clinicalTestCategory = testResult.clinicalTestCategory as
          | "breast_examination"
          | "testicular_examination"
          | "other";
      }

      if (testResult.notes != null) {
        data.notes = testResult.notes;
      }

      operations.push({
        id: testResult.id,
        ...data,
      });
    });

  // Build payload - always include testResults (even if empty) to save hasTestingDone state
  return {
    testResults: operations.length > 0 ? operations : [],
  };
}
